import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../../contexts/ThemeContext.jsx';

const UserDetailsModal = ({ user, onClose, onRefresh }) => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const isCreating = !user;
  const [editing, setEditing] = useState(isCreating);
  const [formData, setFormData] = useState({
    email: user?.user?.email || '',
    first_name: user?.user?.first_name || '',
    last_name: user?.user?.last_name || '',
    business_name: user?.user?.business_name || '',
    street_address_1: user?.user?.street_address_1 || '',
    street_address_2: user?.user?.street_address_2 || '',
    city: user?.user?.city || '',
    state_province: user?.user?.state_province || '',
    country: user?.user?.country || '',
    zip_postal_code: user?.user?.zip_postal_code || '',
    is_admin: user?.user?.is_admin || false
  });
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [assigningPlan, setAssigningPlan] = useState(false);
  
  // Wallet management state
  const [walletData, setWalletData] = useState(null);
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [showWalletDetails, setShowWalletDetails] = useState(false);
  const [promoCredit, setPromoCredit] = useState({ amount: '', reason: '' });
  const [addingCredit, setAddingCredit] = useState(false);

  React.useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const token = await getToken();
      const response = await fetch('https://parrotmate.com/api/admin/plans', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPlans(data.filter(plan => plan.is_current && plan.is_active));
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      
      if (isCreating) {
        // Create new user
        const response = await fetch('https://parrotmate.com/api/admin/users', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          onRefresh();
          onClose();
        }
      } else {
        // Update existing user
        const response = await fetch(`https://parrotmate.com/api/admin/users/${user.user.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          setEditing(false);
          onRefresh();
        }
      }
    } catch (error) {
      console.error(`Failed to ${isCreating ? 'create' : 'update'} user:`, error);
    } finally {
      setSaving(false);
    }
  };

  const handleAssignPlan = async () => {
    if (!selectedPlan) return;
    
    setAssigningPlan(true);
    try {
      const token = await getToken();
      const response = await fetch('https://parrotmate.com/api/admin/users/assign-plan', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user.user.id,
          planVersionId: selectedPlan
        })
      });

      if (response.ok) {
        onRefresh();
        onClose();
      }
    } catch (error) {
      console.error('Failed to assign plan:', error);
    } finally {
      setAssigningPlan(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(`https://parrotmate.com/api/admin/users/${user.user.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        onRefresh();
        onClose();
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const fetchWalletData = async () => {
    if (!user?.user?.id) return;
    
    setLoadingWallet(true);
    try {
      const token = await getToken();
      const response = await fetch(`https://parrotmate.com/api/admin/users/${user.user.id}/wallet`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWalletData(data);
      }
    } catch (error) {
      console.error('Failed to fetch wallet data:', error);
    } finally {
      setLoadingWallet(false);
    }
  };

  const handleAddPromoCredit = async () => {
    if (!promoCredit.amount || !promoCredit.reason.trim()) return;
    
    setAddingCredit(true);
    try {
      const token = await getToken();
      const response = await fetch(`https://parrotmate.com/api/admin/users/${user.user.id}/wallet/credit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount_cents: Math.round(parseFloat(promoCredit.amount) * 100),
          reason: promoCredit.reason.trim()
        })
      });

      if (response.ok) {
        // Reset form
        setPromoCredit({ amount: '', reason: '' });
        // Refresh wallet data
        await fetchWalletData();
      }
    } catch (error) {
      console.error('Failed to add promotional credit:', error);
    } finally {
      setAddingCredit(false);
    }
  };

  // Fetch wallet data when modal opens for existing users
  React.useEffect(() => {
    if (user && !isCreating) {
      fetchWalletData();
    }
  }, [user, isCreating]);

  return (
    <div className={"fixed inset-0 bg-black/75 dark:bg-gray-900/75 flex items-center justify-center z-50"}>
      <div className={"bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"}>
        <div className="flex justify-between items-center mb-6">
          <h3 className={"text-xl font-semibold text-gray-900 dark:text-white"}>
            {isCreating ? 'Create New User' : 'User Details'}
          </h3>
          <button
            onClick={onClose}
            className={"text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"}
          >
            ✕
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* User Information */}
          <div className="space-y-4">
            <h4 className={"text-lg font-medium text-gray-900 dark:text-white border-b pb-2 border-gray-300 dark:border-gray-600"}>User Information</h4>
            
            {editing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={"block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600"}>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className={"mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-300 dark:bg-gray-800"}
                    />
                  </div>
                  <div>
                    <label className={"block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600"}>Business Name <span className="text-gray-500 dark:text-gray-400">(optional)</span></label>
                    <input
                      type="text"
                      value={formData.business_name}
                      onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                      className={"mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-300 dark:bg-gray-800"}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={"block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600"}>First Name</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      className={"mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-300 dark:bg-gray-800"}
                    />
                  </div>
                  <div>
                    <label className={"block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600"}>Last Name</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      className={"mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-300 dark:bg-gray-800"}
                    />
                  </div>
                </div>

                <div>
                  <label className={"block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600"}>Street Address 1</label>
                  <input
                    type="text"
                    value={formData.street_address_1}
                    onChange={(e) => setFormData({...formData, street_address_1: e.target.value})}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                      theme === 'dark'
                        ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-300'
                        : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Street Address 2 <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>(optional)</span></label>
                  <input
                    type="text"
                    value={formData.street_address_2}
                    onChange={(e) => setFormData({...formData, street_address_2: e.target.value})}
                    className={"mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-300 dark:bg-gray-800"}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={"block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600"}>City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      className={"mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-300 dark:bg-gray-800"}
                    />
                  </div>
                  <div>
                    <label className={"block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600"}>State/Province</label>
                    <input
                      type="text"
                      value={formData.state_province}
                      onChange={(e) => setFormData({...formData, state_province: e.target.value})}
                      className={"mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-300 dark:bg-gray-800"}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={"block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600"}>Country</label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({...formData, country: e.target.value})}
                      className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                        theme === 'dark'
                          ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-300'
                          : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={"block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600"}>ZIP/Postal Code</label>
                    <input
                      type="text"
                      value={formData.zip_postal_code}
                      onChange={(e) => setFormData({...formData, zip_postal_code: e.target.value})}
                      className={"mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-300 dark:bg-gray-800"}
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_admin}
                      onChange={(e) => setFormData({...formData, is_admin: e.target.checked})}
                      className={"mr-2 rounded text-teal-600 focus:ring-teal-500 border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-700 dark:bg-gray-800"}
                    />
                    <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Admin User</span>
                  </label>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 transition-colors dark:text-white"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    className={"px-4 py-2 rounded transition-colors bg-gray-500 text-white hover:bg-gray-600 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500 dark:bg-gray-900 dark:text-white"}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : !isCreating ? (
              <div className="space-y-3">
                <div>
                  <span className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Email:</span> <span className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{user.user.email}</span>
                </div>
                <div>
                  <span className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Name:</span> <span className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{user.user.first_name} {user.user.last_name}</span>
                </div>
                {user.user.business_name && (
                  <div>
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Business:</span> <span className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{user.user.business_name}</span>
                  </div>
                )}
                {user.user.street_address_1 && (
                  <div>
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Address:</span>
                    <span className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {user.user.street_address_1}
                      {user.user.street_address_2 && `, ${user.user.street_address_2}`}
                      <br />
                      {user.user.city}, {user.user.state_province} {user.user.zip_postal_code}
                      <br />
                      {user.user.country}
                    </span>
                  </div>
                )}
                <div>
                  <span className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Admin:</span> <span className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{user.user.is_admin ? 'Yes' : 'No'}</span>
                </div>
                <div>
                  <span className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Created:</span> <span className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{new Date(user.user.created_at).toLocaleDateString()}</span>
                </div>
                {user.user.subscription_status && (
                  <div>
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Subscription:</span>
                    <span className={`ml-2 px-2 py-1 rounded text-xs ${
                      user.user.subscription_status === 'active' 
                        ? theme === 'dark' ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-800'
                        : user.user.subscription_status === 'trialing' 
                        ? theme === 'dark' ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-800'
                        : theme === 'dark' ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.user.subscription_status}
                    </span>
                  </div>
                )}
                {user.user.plan_name && (
                  <div>
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Plan:</span> <span className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{user.user.plan_name} (${user.user.plan_price / 100}/month)</span>
                  </div>
                )}
                <button
                  onClick={() => setEditing(true)}
                  className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors dark:text-white"
                >
                  Edit User
                </button>
              </div>
            ) : null}

            {/* Plan Assignment - only show for existing users */}
            {!isCreating && (
              <div className={`mt-6 pt-6 border-t ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>
                <h5 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-3`}>Assign Plan</h5>
                <div className="flex space-x-2">
                  <select
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    className={`flex-1 border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                      theme === 'dark'
                        ? 'border-gray-600 bg-gray-700 text-white'
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  >
                    <option value="">Select a plan...</option>
                    {plans.map((plan) => (
                      <option key={plan.version_id} value={plan.version_id}>
                        {plan.name} - ${plan.monthly_fee / 100}/month
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleAssignPlan}
                    disabled={!selectedPlan || assigningPlan}
                    className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 transition-colors dark:text-white"
                  >
                    {assigningPlan ? 'Assigning...' : 'Assign'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User Resources - only show for existing users */}
          {!isCreating && (
            <div className="space-y-6">
            {/* Phone Numbers */}
            <div>
              <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-white border-gray-600' : 'text-gray-900 border-gray-300'} border-b pb-2 mb-3`}>Phone Numbers ({user.phoneNumbers.length})</h4>
              <div className="space-y-2">
                {user.phoneNumbers.map((phone) => (
                  <div key={phone.id} className={`p-3 rounded border ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{phone.number}</div>
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{phone.friendly_name}</div>
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Purchased: {new Date(phone.purchased_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
                {user.phoneNumbers.length === 0 && (
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} text-sm`}>No phone numbers assigned</p>
                )}
              </div>
            </div>

            {/* Parrots */}
            <div>
              <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-white border-gray-600' : 'text-gray-900 border-gray-300'} border-b pb-2 mb-3`}>Parrots ({user.agents.length})</h4>
              <div className="space-y-2">
                {user.agents.map((parrot) => (
                  <div key={parrot.id} className={`p-3 rounded border ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{parrot.name}</div>
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} truncate`}>{parrot.first_message}</div>
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Voice: {parrot.voice_id} | Model: {parrot.model_id}
                    </div>
                  </div>
                ))}
                {user.agents.length === 0 && (
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} text-sm`}>No parrots configured</p>
                )}
              </div>
            </div>

            {/* Call Forwarding */}
            <div>
              <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-white border-gray-600' : 'text-gray-900 border-gray-300'} border-b pb-2 mb-3`}>Call Forwarding ({user.forwarding.length})</h4>
              <div className="space-y-2">
                {user.forwarding.map((rule) => (
                  <div key={rule.id} className={`p-3 rounded border ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{rule.phone_number}</div>
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>→ {rule.parrot_name || rule.agent_name}</div>
                  </div>
                ))}
                {user.forwarding.length === 0 && (
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} text-sm`}>No forwarding rules configured</p>
                )}
              </div>
            </div>

            {/* Wallet Management */}
            <div>
              <div className={`flex justify-between items-center border-b ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'} pb-2 mb-3`}>
                <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Wallet & Billing</h4>
                <button
                  onClick={() => setShowWalletDetails(!showWalletDetails)}
                  className="text-sm text-teal-400 hover:text-teal-300 transition-colors"
                >
                  {showWalletDetails ? 'Hide Details' : 'Show Details'}
                </button>
              </div>
              
              {loadingWallet ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500 mx-auto"></div>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} text-sm mt-2`}>Loading wallet data...</p>
                </div>
              ) : walletData ? (
                <div className="space-y-4">
                  {/* Wallet Balance */}
                  <div className={`p-3 rounded border ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex justify-between items-center">
                      <span className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Current Balance:</span>
                      <span className={`text-lg font-bold ${
                        (walletData.wallet?.balance_cents || 0) > 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        ${((walletData.wallet?.balance_cents || 0) / 100).toFixed(2)}
                      </span>
                    </div>
                    {walletData.billing_preferences && (
                      <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                        Plan: {walletData.billing_preferences.plan_name} - ${(walletData.billing_preferences.monthly_fee / 100).toFixed(2)}/month
                      </div>
                    )}
                  </div>

                  {/* Add Promotional Credit */}
                  <div className={`p-3 rounded border ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <h5 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-3`}>Add Promotional Credit</h5>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Amount ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={promoCredit.amount}
                            onChange={(e) => setPromoCredit({...promoCredit, amount: e.target.value})}
                            className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                              theme === 'dark'
                                ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-300'
                                : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                            }`}
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Reason</label>
                          <input
                            type="text"
                            value={promoCredit.reason}
                            onChange={(e) => setPromoCredit({...promoCredit, reason: e.target.value})}
                            className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                              theme === 'dark'
                                ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-300'
                                : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                            }`}
                            placeholder="e.g., Customer service credit"
                          />
                        </div>
                      </div>
                      <button
                        onClick={handleAddPromoCredit}
                        disabled={addingCredit || !promoCredit.amount || !promoCredit.reason.trim()}
                        className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:text-white"
                      >
                        {addingCredit ? 'Adding Credit...' : 'Add Promotional Credit'}
                      </button>
                    </div>
                  </div>

                  {/* Wallet Details */}
                  {showWalletDetails && (
                    <div className="space-y-3">
                      {/* Recent Transactions */}
                      <div className={`p-3 rounded border ${
                        theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <h5 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-3`}>Recent Transactions</h5>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {walletData.transactions && walletData.transactions.length > 0 ? (
                            walletData.transactions.slice(0, 10).map((transaction) => (
                              <div key={transaction.id} className="flex justify-between items-center text-sm">
                                <div>
                                  <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                                    transaction.amount_cents > 0 ? 'bg-green-400' : 'bg-red-400'
                                  }`}></span>
                                  <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{transaction.description}</span>
                                </div>
                                <div className="text-right">
                                  <div className={`font-medium ${
                                    transaction.amount_cents > 0 ? 'text-green-400' : 'text-red-400'
                                  }`}>
                                    {transaction.amount_cents > 0 ? '+' : ''}${(transaction.amount_cents / 100).toFixed(2)}
                                  </div>
                                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {new Date(transaction.created_at).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} text-sm`}>No transactions found</p>
                          )}
                        </div>
                      </div>

                      {/* Monthly Usage History */}
                      {walletData.monthly_history && walletData.monthly_history.length > 0 && (
                        <div className={`p-3 rounded border ${
                          theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                        }`}>
                          <h5 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-3`}>Monthly Usage History</h5>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {walletData.monthly_history.slice(0, 6).map((month) => (
                              <div key={month.id} className="flex justify-between items-center text-sm">
                                <div>
                                  <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{month.month_year}</span>
                                  {month.plan_name && (
                                    <span className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} ml-2`}>({month.plan_name})</span>
                                  )}
                                </div>
                                <div className="text-right">
                                  <div className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                    {month.minutes_used || 0}/{month.minutes_included || 0} min
                                  </div>
                                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                                    ${((month.plan_fee_cents || 0) / 100).toFixed(2)} plan fee
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className={`p-3 rounded border ${
                  theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} text-sm`}>No wallet data available</p>
                </div>
              )}
            </div>
          </div>
          )}
        </div>

        {/* Actions */}
        <div className={`mt-8 pt-6 border-t ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'} flex justify-between`}>
          {!isCreating && (
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors dark:text-white"
            >
              Delete User
            </button>
          )}
          <div className={!isCreating ? '' : 'w-full flex justify-end'}>
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded transition-colors ${
                theme === 'dark'
                  ? 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                  : 'bg-gray-500 text-white hover:bg-gray-600'
              }`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal;
